import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, ArrowLeft, Clock, Lock, User, Phone, Mail, Calendar, MapPin, Star, MessageSquare, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const QUICK_REPLIES = [
  "Namaste 🙏 I've reviewed your chart. May I begin with what you'd like guidance on first?",
  "Could you share your exact date, time, and place of birth so I can analyse your chart precisely?",
  "Based on your current Dasha, I can see why this period feels challenging. Let me explain…",
  "I'll suggest a simple remedy for this — please share if you'd like a mantra-based or a gem-based approach.",
  "Thank you for sharing. May the stars guide you to clarity and peace ✨",
];

function formatRemaining(ms) {
  if (ms <= 0) return "Expired";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function LiveChatPage() {
  const { chatId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState({ status: "active", expires_at: null, astrologer_first_reply_ts: null, booking_ref: "", concern: "" });
  const [now, setNow] = useState(() => Date.now());
  const [closing, setClosing] = useState(false);
  const [context, setContext] = useState(null);
  const [showContext, setShowContext] = useState(true);
  const lastTsRef = useRef("");
  const bottomRef = useRef(null);
  const audioRef = useRef(null);
  const titleFlashRef = useRef(null);
  const originalTitleRef = useRef(typeof document !== "undefined" ? document.title : "");
  const prevMsgLenRef = useRef(0);

  const isAdmin = user?.role === "admin";

  // Fetch rich customer context (admin only) — once per chatId
  useEffect(() => {
    if (!isAdmin) return;
    let cancel = false;
    axios.get(`${API}/admin/livechats/${chatId}/context`, { withCredentials: true })
      .then(res => { if (!cancel) setContext(res.data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [isAdmin, chatId]);

  // Audio "ding" + tab title flash on new INCOMING messages
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Lazy-init a short beep using WebAudio (no asset needed)
    audioRef.current = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine"; o.frequency.value = 880;
        g.gain.value = 0.0001;
        g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.28);
        o.start(); o.stop(ctx.currentTime + 0.3);
      } catch (_) { /* ignore – e.g. autoplay blocked */ }
    };
    return () => { if (titleFlashRef.current) { clearInterval(titleFlashRef.current); document.title = originalTitleRef.current; } };
  }, []);

  // Detect new incoming messages -> beep + flash tab title
  useEffect(() => {
    if (messages.length <= prevMsgLenRef.current) {
      prevMsgLenRef.current = messages.length;
      return;
    }
    const newOnes = messages.slice(prevMsgLenRef.current);
    prevMsgLenRef.current = messages.length;
    const incoming = newOnes.filter(m =>
      (isAdmin && m.sender === "customer") || (!isAdmin && m.sender === "admin")
    );
    if (!incoming.length) return;
    // Beep
    if (typeof audioRef.current === "function") audioRef.current();
    // Tab title flash only when tab is hidden
    if (typeof document !== "undefined" && document.hidden) {
      if (titleFlashRef.current) clearInterval(titleFlashRef.current);
      let toggle = false;
      const flashTitle = `🔔 New message · AstroFate`;
      titleFlashRef.current = setInterval(() => {
        document.title = toggle ? originalTitleRef.current : flashTitle;
        toggle = !toggle;
      }, 1000);
      const onFocus = () => {
        if (titleFlashRef.current) { clearInterval(titleFlashRef.current); titleFlashRef.current = null; }
        document.title = originalTitleRef.current;
        window.removeEventListener("focus", onFocus);
      };
      window.addEventListener("focus", onFocus);
    }
  }, [messages, isAdmin]);

  useEffect(() => {
    // user === undefined means auth is still loading; only redirect on definitive null
    if (user === null) { navigate("/auth?redirect=/livechat/" + chatId); }
  }, [user, chatId, navigate]);

  // Poll every 3s
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const params = lastTsRef.current ? { since: lastTsRef.current } : {};
        const res = await axios.get(`${API}/livechat/${chatId}/messages`, { params, withCredentials: true });
        if (cancelled) return;
        setMeta({
          status: res.data.status,
          expires_at: res.data.expires_at,
          astrologer_first_reply_ts: res.data.astrologer_first_reply_ts,
          booking_ref: res.data.booking_ref,
          concern: res.data.concern,
        });
        if (res.data.messages?.length) {
          setMessages(prev => [...prev, ...res.data.messages]);
          lastTsRef.current = res.data.messages[res.data.messages.length - 1].ts;
        }
      } catch (e) {
        if (e.response?.status === 404) setError("Chat not found.");
        else if (e.response?.status === 403) setError("You don't have access to this chat.");
      }
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [chatId]);

  // Ticking clock for countdown
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const expiresMs = meta.expires_at ? new Date(meta.expires_at).getTime() - now : null;
  const isClosed = meta.status === "closed" || (expiresMs !== null && expiresMs <= 0);
  const waitingForAstrologer = !meta.astrologer_first_reply_ts && !isClosed;

  const send = async () => {
    if (!input.trim() || sending || isClosed) return;
    const text = input.trim();
    setSending(true);
    setInput("");
    try {
      const res = await axios.post(`${API}/livechat/${chatId}/messages`, { text }, { withCredentials: true });
      setMessages(prev => [...prev, res.data]);
      lastTsRef.current = res.data.ts;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not send. Please try again.");
      setInput(text);
    } finally { setSending(false); }
  };

  const closeSession = async () => {
    if (!isAdmin || closing) return;
    if (!window.confirm("Close this consultation session for the customer? They will need to book again.")) return;
    setClosing(true);
    try {
      await axios.post(`${API}/livechat/${chatId}/close`, {}, { withCredentials: true });
      setMeta(m => ({ ...m, status: "closed" }));
    } catch (e) {
      setError(e.response?.data?.detail || "Could not close the session.");
    } finally { setClosing(false); }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  // Status banner colour
  let bannerColor = "#86efac";   // active green
  let bannerBg = "rgba(34,197,94,0.12)";
  let bannerText = "● Active";
  if (waitingForAstrologer) { bannerColor = "#fdba74"; bannerBg = "rgba(249,115,22,0.15)"; bannerText = "● Waiting for astrologer"; }
  if (isClosed) { bannerColor = "#fca5a5"; bannerBg = "rgba(239,68,68,0.15)"; bannerText = "● Session ended"; }

  return (
    <div style={{ minHeight: "100vh", background: "#120021", paddingTop: "70px", paddingBottom: "80px" }}>
      <div style={{ maxWidth: isAdmin ? "1180px" : "780px", margin: "0 auto", padding: "12px 14px" }}>
        {/* Header */}
        <div className="glass-card" style={{ padding: "14px 16px", marginBottom: "12px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
          <button onClick={() => navigate(isAdmin ? "/admin" : "/dashboard")} className="btn-ghost" style={{ padding: "6px 10px", fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "6px" }}>
            <ArrowLeft size={14} /> {isAdmin ? "Admin" : "Dashboard"}
          </button>
          <div className="font-cinzel" style={{ color: "#FFD166", fontSize: "1rem" }}>Live Astrologer Chat</div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {isAdmin && (
              <button data-testid="toggle-context-btn" onClick={() => setShowContext(s => !s)} className="btn-ghost" style={{ padding: "5px 10px", fontSize: "0.72rem" }}>
                {showContext ? "Hide" : "Show"} Customer Panel
              </button>
            )}
            <span data-testid="livechat-status" style={{ background: bannerBg, color: bannerColor, padding: "3px 10px", borderRadius: "50px", fontSize: "0.7rem", fontWeight: 600 }}>{bannerText}</span>
          </div>
        </div>

        {/* Session info bar (timer + concern + booking ref) */}
        <div className="glass-card" style={{ padding: "10px 14px", marginBottom: "12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            {meta.booking_ref && <span style={{ color: "#FFD166", fontSize: "0.78rem", fontWeight: 600 }}>#{meta.booking_ref}</span>}
            {meta.concern && <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.78rem" }}>· {meta.concern}</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {expiresMs !== null && !isClosed && (
              <span data-testid="livechat-timer" style={{ display: "inline-flex", alignItems: "center", gap: "6px", color: expiresMs < 5 * 60 * 1000 ? "#fca5a5" : "#FFD166", fontSize: "0.85rem", fontWeight: 700, fontFamily: "ui-monospace, monospace" }}>
                <Clock size={13} /> {formatRemaining(expiresMs)}
              </span>
            )}
            {isAdmin && !isClosed && (
              <button data-testid="livechat-close-btn" onClick={closeSession} className="btn-ghost" style={{ padding: "5px 10px", fontSize: "0.75rem", color: "#fca5a5", borderColor: "rgba(239,68,68,0.3)" }}>
                {closing ? "Closing…" : "Close Session"}
              </button>
            )}
          </div>
        </div>

        {error && <div style={{ background: "rgba(239,68,68,0.12)", color: "#fca5a5", padding: "10px 14px", borderRadius: "10px", fontSize: "0.85rem", marginBottom: "12px" }}>{error}</div>}

        {/* Main grid — chat on the left, context panel on the right (admin only) */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isAdmin && showContext ? "minmax(0,1fr) 320px" : "minmax(0,1fr)",
          gap: "14px",
        }}>
          <div style={{ minWidth: 0 }}>
            {/* Messages */}
            <div className="glass-card" data-testid="livechat-messages" style={{ padding: "16px", minHeight: "55vh", maxHeight: "60vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "10px" }}>
              {messages.length === 0 && (
                <div style={{ textAlign: "center", color: "rgba(255,255,255,0.5)", fontSize: "0.86rem", padding: "30px 10px" }}>
                  {isAdmin ? "Send the first message to start the 30-minute consultation window." : "Your astrologer will join here shortly. Feel free to send your first message."}
                </div>
              )}
              {messages.map((m, i) => {
                const mine = (isAdmin && m.sender === "admin") || (!isAdmin && m.sender === "customer");
                const msgKey = `${m.timestamp || ""}-${m.sender}-${i}`;
                return (
                  <div key={msgKey} data-testid={`livechat-msg-${m.sender}`} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
                    <div style={{
                      maxWidth: "75%",
                      padding: "9px 14px",
                      borderRadius: mine ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                      background: mine ? "linear-gradient(135deg, rgba(255,209,102,0.18), rgba(255,209,102,0.08))" : "rgba(255,255,255,0.06)",
                      border: `1px solid ${mine ? "rgba(255,209,102,0.3)" : "rgba(157,78,221,0.3)"}`,
                      color: "rgba(255,255,255,0.92)",
                      fontSize: "0.9rem",
                      lineHeight: 1.55,
                      whiteSpace: "pre-wrap",
                    }}>
                      <div style={{ fontSize: "0.7rem", color: mine ? "#FFD166" : "#c084fc", marginBottom: "3px", fontWeight: 600 }}>
                        {m.sender === "admin" ? "Astrologer" : "Customer"}{mine ? " (you)" : ""}
                      </div>
                      {m.text}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Quick reply pills — admin only */}
            {isAdmin && !isClosed && (
              <div data-testid="quick-replies" style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
                {QUICK_REPLIES.map((q, i) => (
                  <button
                    key={q}
                    data-testid={`quick-reply-${i}`}
                    type="button"
                    onClick={() => setInput(q)}
                    style={{
                      background: "rgba(157,78,221,0.12)",
                      border: "1px solid rgba(157,78,221,0.3)",
                      color: "rgba(255,255,255,0.85)",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      fontSize: "0.74rem",
                      cursor: "pointer",
                      maxWidth: "260px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={q}
                  >
                    <Sparkles size={11} style={{ display: "inline", marginRight: "4px", verticalAlign: "middle", color: "#FFD166" }} />
                    {q.length > 38 ? q.slice(0, 38) + "…" : q}
                  </button>
                ))}
              </div>
            )}

            {/* Input or ended-state */}
            {isClosed ? (
              <div className="glass-card" data-testid="livechat-ended" style={{ padding: "16px 18px", marginTop: "10px", border: "1px solid rgba(239,68,68,0.25)", background: "linear-gradient(135deg, rgba(239,68,68,0.08), transparent)", textAlign: "center" }}>
                <Lock size={20} style={{ color: "#fca5a5", marginBottom: "8px" }} />
                <div style={{ color: "#fca5a5", fontWeight: 700, marginBottom: "4px" }}>Consultation session has ended</div>
                <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", marginBottom: "14px" }}>
                  {isAdmin ? "The customer can book a fresh consultation to continue." : "Book another consultation to continue speaking with your astrologer."}
                </div>
                {!isAdmin && (
                  <button data-testid="livechat-rebook-btn" onClick={() => navigate("/consultation")} className="btn-gold" style={{ padding: "9px 22px", fontSize: "0.86rem" }}>Book Again</button>
                )}
              </div>
            ) : (
              <div className="glass-card" style={{ padding: "10px 14px", display: "flex", gap: "10px", alignItems: "flex-end", marginTop: "10px" }}>
                <textarea
                  data-testid="livechat-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder={waitingForAstrologer && !isAdmin ? "Type your question (astrologer hasn't joined yet)..." : "Type your message..."}
                  rows={1}
                  style={{ flex: 1, background: "transparent", border: 0, outline: 0, color: "white", fontFamily: "'DM Sans', sans-serif", fontSize: "0.92rem", resize: "none", lineHeight: 1.5, maxHeight: "100px", overflowY: "auto" }}
                />
                <button data-testid="livechat-send-btn" onClick={send} disabled={!input.trim() || sending} className="btn-gold" style={{ padding: "8px 14px", opacity: !input.trim() || sending ? 0.5 : 1 }}>
                  <Send size={15} />
                </button>
              </div>
            )}
          </div>

          {/* Customer Context Panel (admin only) */}
          {isAdmin && showContext && (
            <aside data-testid="customer-context-panel" className="glass-card" style={{ padding: "16px", height: "fit-content", border: "1px solid rgba(157,78,221,0.25)" }}>
              <div className="font-cinzel" style={{ color: "#FFD166", fontSize: "0.95rem", marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                <User size={14} /> Customer Profile
              </div>
              {!context && <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.8rem" }}>Loading…</div>}
              {context && (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px", fontSize: "0.82rem", color: "rgba(255,255,255,0.85)" }}>
                  <div data-testid="ctx-customer">
                    <div style={{ fontWeight: 700, color: "white", marginBottom: "4px" }}>{context.customer?.name || "Guest"}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "rgba(255,255,255,0.65)", marginBottom: "3px" }}>
                      <Mail size={12} /> {context.customer?.email || "—"}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "rgba(255,255,255,0.65)" }}>
                      <Phone size={12} /> {context.customer?.phone || "—"}
                    </div>
                  </div>

                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "12px" }}>
                    <div style={{ color: "#c084fc", fontWeight: 600, fontSize: "0.74rem", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "6px" }}>Booking</div>
                    <div>Ref: <span style={{ color: "#FFD166", fontWeight: 600 }}>#{context.booking?.ref}</span></div>
                    <div>Concern: {context.booking?.concern || "—"}</div>
                    {context.booking?.date && <div>{context.booking.date} · {context.booking?.time_slot}</div>}
                    <div>Amount: {context.booking?.amount ? `₹${context.booking.amount}` : "—"}</div>
                  </div>

                  {context.birth_details && (
                    <div data-testid="ctx-birth" style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "12px" }}>
                      <div style={{ color: "#c084fc", fontWeight: 600, fontSize: "0.74rem", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: "6px" }}>Birth Details</div>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <Calendar size={12} />
                        {context.birth_details.day}/{context.birth_details.month}/{context.birth_details.year}
                        {context.birth_details.hour !== undefined && (
                          <span style={{ color: "rgba(255,255,255,0.6)" }}>
                            {" "}· {String(((context.birth_details.hour + 11) % 12) + 1).padStart(2, "0")}:
                            {String(context.birth_details.minute || 0).padStart(2, "0")}
                            {" "}{context.birth_details.hour < 12 ? "AM" : "PM"}
                          </span>
                        )}
                      </div>
                      {context.birth_details.city && (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "4px" }}>
                          <MapPin size={12} /> {context.birth_details.city}
                        </div>
                      )}
                    </div>
                  )}

                  {context.kundli_summary && (
                    <details style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "12px" }}>
                      <summary style={{ color: "#c084fc", fontWeight: 600, fontSize: "0.74rem", letterSpacing: "0.5px", textTransform: "uppercase", cursor: "pointer", marginBottom: "6px" }}>
                        Kundli Snippet
                      </summary>
                      <div data-testid="ctx-kundli" style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.78rem", lineHeight: 1.55, marginTop: "8px", whiteSpace: "pre-wrap" }}>
                        {context.kundli_summary}
                      </div>
                    </details>
                  )}

                  <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "12px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px", textAlign: "center" }}>
                    <div>
                      <div style={{ color: "#FFD166", fontWeight: 700, fontSize: "1.1rem" }}>{context.stats?.past_consultations || 0}</div>
                      <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.66rem", textTransform: "uppercase" }}>Bookings</div>
                    </div>
                    <div>
                      <div style={{ color: "#FFD166", fontWeight: 700, fontSize: "1.1rem" }}>{context.stats?.past_chats || 0}</div>
                      <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.66rem", textTransform: "uppercase" }}>Chats</div>
                    </div>
                    <div>
                      <div style={{ color: "#86efac", fontWeight: 700, fontSize: "1.1rem" }}>{context.stats?.completed_payments || 0}</div>
                      <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.66rem", textTransform: "uppercase" }}>Paid</div>
                    </div>
                  </div>

                  {context.whatsapp_url && (
                    <a data-testid="ctx-whatsapp" href={context.whatsapp_url} target="_blank" rel="noopener noreferrer"
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", background: "#25D366", color: "white", textDecoration: "none", padding: "8px 12px", borderRadius: "8px", fontWeight: 600, fontSize: "0.82rem" }}>
                      <MessageSquare size={13} /> WhatsApp Customer
                    </a>
                  )}
                </div>
              )}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
